import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';

// Register all Chart.js components once (idempotent — safe to call multiple times)
ChartJS.register(
  CategoryScale, LinearScale,
  BarElement, LineElement, PointElement,
  Title, Tooltip, Legend, Filler
);

// ── Helpers ───────────────────────────────────────────────────────────────────

const toTitle = (key) =>
  (key ?? '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

/** Indian number formatting: 4,70,452 */
const formatIN = (val) =>
  new Intl.NumberFormat('en-IN').format(Number(val));

/** Y-axis tick abbreviation with Indian scale (L = lakh, K = thousand) */
const tickLabel = (val, isMonetary) => {
  const prefix = isMonetary ? '₹' : '';
  if (val >= 100000) return `${prefix}${(val / 100000).toFixed(1)}L`;
  if (val >= 1000)   return `${prefix}${(val / 1000).toFixed(0)}K`;
  return `${prefix}${formatIN(val)}`;
};

// Detect monetary columns by name
const isMonetaryCol = (key) =>
  /amount|profit|revenue|sales|value|cost|price|balance|gross|net|total/i.test(key ?? '');

// Corporate indigo accent — matches app theme
const ACCENT       = '#6366f1';
const ACCENT_LIGHT = 'rgba(99,102,241,0.12)';
const GRID_COLOR   = '#f1f5f9';
const TICK_COLOR   = '#94a3b8';
const FONT_FAMILY  = "'Inter', 'Segoe UI', sans-serif";

// ── ChartRenderer ─────────────────────────────────────────────────────────────
/**
 * Props:
 *   chart  — { chart_type: 'bar'|'line'|'area', title, x_axis, y_axis }
 *   rows   — array of data objects (same array as the table, from API's `data` field)
 */
const ChartRenderer = ({ chart, rows }) => {
  if (!chart || !rows?.length) return null;

  const { chart_type, title, x_axis, y_axis } = chart;
  if (!x_axis || !y_axis) return null;

  const isArea     = chart_type === 'area';
  const isBar      = chart_type === 'bar';
  const isMonetary = isMonetaryCol(y_axis);

  const labels = rows.map(r => String(r[x_axis] ?? ''));
  const values = rows.map(r => Number(r[y_axis]) || 0);

  // ── Dataset ────────────────────────────────────────────────────────────────
  const dataset = {
    label: toTitle(y_axis),
    data: values,
    borderColor: ACCENT,
    borderWidth: isBar ? 1.5 : 2,

    // Bar-specific
    backgroundColor: isBar
      ? labels.map((_, i) => i % 2 === 0 ? 'rgba(99,102,241,0.80)' : 'rgba(99,102,241,0.55)')
      : ACCENT_LIGHT,
    borderRadius:    isBar ? 5 : 0,
    borderSkipped:   false,

    // Line/area-specific
    fill:              isArea,
    tension:           0.4,
    pointRadius:       isBar ? 0 : 4,
    pointHoverRadius:  6,
    pointBackgroundColor: '#fff',
    pointBorderColor:     ACCENT,
    pointBorderWidth:     2,
  };

  const chartData = { labels, datasets: [dataset] };

  // ── Options ────────────────────────────────────────────────────────────────
  const options = {
    responsive:          true,
    maintainAspectRatio: false,
    animation: { duration: 500, easing: 'easeInOutQuart' },

    plugins: {
      legend: { display: false },

      title: {
        display:  !!title,
        text:     title ?? '',
        font:     { size: 12, weight: '600', family: FONT_FAMILY },
        color:    '#0f172a',
        padding:  { bottom: 10 },
        align:    'start',
      },

      tooltip: {
        backgroundColor: '#1e293b',
        titleColor:      '#94a3b8',
        bodyColor:       '#f1f5f9',
        borderColor:     '#334155',
        borderWidth:     1,
        padding:         10,
        cornerRadius:    8,
        callbacks: {
          title: (items) => items[0]?.label ?? '',
          label: (ctx) => {
            const raw = ctx.parsed?.y ?? ctx.raw ?? 0;
            return `  ${toTitle(y_axis)}: ${isMonetary ? '₹' : ''}${formatIN(raw)}`;
          },
        },
      },
    },

    scales: {
      x: {
        grid:   { display: false },
        border: { display: false },
        ticks: {
          color:         TICK_COLOR,
          font:          { size: 10, family: FONT_FAMILY },
          maxRotation:   labels.length > 7 ? 45 : 0,
          maxTicksLimit: 12,
        },
      },
      y: {
        grid:   { color: GRID_COLOR },
        border: { display: false, dash: [3, 3] },
        ticks: {
          color: TICK_COLOR,
          font:  { size: 10, family: FONT_FAMILY },
          callback: (val) => tickLabel(val, isMonetary),
          maxTicksLimit: 6,
        },
        beginAtZero: true,
      },
    },
  };

  const ChartComponent = isBar ? Bar : Line;

  return (
    <div
      style={{
        marginBottom: '10px',
        borderRadius: '12px',
        border:       '1px solid #e2e8f0',
        background:   '#ffffff',
        padding:      '14px 14px 6px',
        overflow:     'hidden',
      }}
    >
      {/* Chart title (mobile: smaller) */}
      {title && (
        <p style={{
          fontSize:     '12px',
          fontWeight:   700,
          color:        '#0f172a',
          marginBottom: '8px',
          letterSpacing: '0',
        }}>
          {title}
        </p>
      )}

      {/* Chart canvas */}
      <div style={{ height: '200px', position: 'relative' }}>
        <ChartComponent data={chartData} options={{ ...options, plugins: { ...options.plugins, title: { display: false } } }} />
      </div>

      {/* X-axis label */}
      <p style={{
        textAlign:  'center',
        fontSize:   '10px',
        color:      TICK_COLOR,
        marginTop:  '4px',
        fontWeight: 500,
      }}>
        {toTitle(x_axis)}
      </p>
    </div>
  );
};

export default ChartRenderer;
